import { HeroWithImageProps } from "/Models/Generated/HeroWithImageProps";
import "./HeroWithImage.scss";

import { useEffect, useRef, useState } from "react";
import {
  GridPresetOneHalf,
  GridRow,
  Picture,
  Labels,
  VideoWithPoster,
  PrintAndShare,
  PublishedAndUpdated,
  OptimizelyEditProperty,
  HeroWithImageText,
} from "/App.Components";
import { createSpreadableBem } from "/Helpers/createBem";

const HeroWithImage = ({
  labels,
  heading,
  headingPropertyName,
  image,
  video,
  publishedAndUpdated,
  printAndShare,
}: HeroWithImageProps) => {
  const [posterClicked, setPosterClicked] = useState(false);
  const heroRef = useRef<HTMLDivElement>(null);
  const textContainerRef = useRef<HTMLDivElement>(null);
  const textContainerInnerRef = useRef<HTMLDivElement>(null);
  const imageContainerRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (typeof window === "undefined") return;
    const textContainer = textContainerRef.current;
    const textContainerInner = textContainerInnerRef.current;
    const imageContainer = imageContainerRef.current;
    if (!imageContainer || !textContainer || !textContainerInner) return;

    const alignBylineAndImageText = () => {
      if (window.innerWidth < 1024 && textContainer.style) {
        (textContainer.style as { minHeight: string | null }).minHeight = null;
        (textContainerInner.style as { minHeight: string | null }).minHeight =
          null;
      } else {
        const height = Number.parseInt(
          window.getComputedStyle(imageContainer).height.replace("px", ""),
          10
        );
        const innerHeight = labels ? height - 28 : height;
        (textContainer.style as { minHeight: string | null }).minHeight =
          height + "px";
        (textContainerInner.style as { minHeight: string | null }).minHeight =
          innerHeight + "px";
      }
    };

    alignBylineAndImageText();

    window.addEventListener("resize", alignBylineAndImageText);

    return () => window.removeEventListener("resize", alignBylineAndImageText);
  }, []);

  const bem = createSpreadableBem("hero-with-image-norad");

  return (
    <div {...bem("")} ref={heroRef}>
      <div
        {...bem("__content", {
          "--no-media": !image && !video,
        })}
      >
        <GridRow>
          <GridPresetOneHalf>
            <div {...bem("__text")} ref={textContainerRef}>
              <div {...bem("__text-inner")} ref={textContainerInnerRef}>
                {labels && (
                  <div {...bem("__tags")}>
                    <Labels {...labels} />
                  </div>
                )}
                <div {...bem("__heading-container")}>
                  <OptimizelyEditProperty propertyName={headingPropertyName}>
                    {heading && (
                      <h1
                        {...bem("__heading", {
                          "--no-media": !video && !image,
                          "--with-labels": labels,
                        })}
                      >
                        {heading}
                      </h1>
                    )}
                  </OptimizelyEditProperty>
                </div>
              </div>
            </div>
            {(publishedAndUpdated || printAndShare) && (
              <div {...bem("__byline")}>
                {publishedAndUpdated && (
                  <PublishedAndUpdated {...publishedAndUpdated} />
                )}
                {publishedAndUpdated && printAndShare && (
                  <div {...bem("__byline-divider")} />
                )}
                {printAndShare && <PrintAndShare {...printAndShare} />}
              </div>
            )}
          </GridPresetOneHalf>
          {(image || video) && (
            <GridPresetOneHalf>
              <div {...bem("__thumbnail")} ref={imageContainerRef}>
                {image && !video && (
                  <Picture
                    {...image}
                    pictureAspectRatio="16/9"
                    pictureBehaviour="Cover"
                    eagerLoading
                  />
                )}
                {video && (
                  <VideoWithPoster
                    posterClicked={posterClicked}
                    setPosterClicked={() => setPosterClicked(!posterClicked)}
                    {...video}
                    posterBehaviour="Cover"
                    posterAspectRatio="16/9"
                    eagerLoading
                  />
                )}
              </div>
              <HeroWithImageText {...{ image, video, posterClicked }} />
            </GridPresetOneHalf>
          )}
        </GridRow>
      </div>
    </div>
  );
};

export default HeroWithImage;
